import { Button }               from 'antd';
import type { FormInstance }    from 'antd';
import React                    from 'react';
import { observer }             from 'mobx-react';
import { Col }                  from 'antd';
import Row                      from 'components/App/Row';
import { Form }                 from 'antd';
import { ApiCollection }        from 'collections/ApiCollection';
import InputPassword            from 'components/App/IntlInputPassword';
import { EN }                   from 'constants/Languages';
import { FR }                   from 'constants/Languages';
import { ROLE_ORGANIZER }       from 'constants/RoleReferences';
import Input                    from 'components/App/IntlInput';
import Select                   from 'components/App/IntlSelect';
import FormattedMessageWrapper  from 'components/App/FormattedMessageWrapper';
import UserShowModel            from 'models/UserShowModel';
import handleApiError           from 'utils/handleApiError';
import handleFormError          from 'utils/handleFormError';
import FormItem                 from 'components/App/FormItem';
import UserModel                from 'models/UserModel';
import ShowModel                from 'models/ShowModel';
import RoleModel                from 'models/RoleModel';
import { messageSuccessIntl }   from 'utils/messageIntl';
import { AppFormComponent }     from 'components/App/AppForm';
import { AppFormInjectedProps } from 'components/App/AppForm';

interface OrganizerFormProps {
  show: ShowModel;
  user: UserModel;
  userShow: UserShowModel;
}

interface OrganizerFormState {
  loading: boolean;
  selectedUser: UserModel;
}

@observer
class OrganizerForm extends AppFormComponent<OrganizerFormProps, OrganizerFormState> {
  public state: OrganizerFormState = {
    loading: false,
    selectedUser: new UserModel(),
  };

  private formRef: React.RefObject<FormInstance> = React.createRef<FormInstance>();

  private roleCollection = new ApiCollection(RoleModel);

  private userCollection = new ApiCollection(UserModel);

  public componentDidMount() {
    this.setInitialValues();
    this.roleCollection.setFilter('reference', ROLE_ORGANIZER).list();
    this.updateFooter();
  }

  public componentDidUpdate(
    prevProps: OrganizerFormProps & AppFormInjectedProps,
    prevState: OrganizerFormState,
  ) {
    if (prevProps.user.id !== this.props.user.id) {
      this.setInitialValues();
    }
    if (prevState.loading !== this.state.loading) {
      this.updateFooter();
    }
  }

  public render() {
    return (
      <div className="modal-form">
        {this.renderOrganizerSelector()}

        <Row>
          <Col span={24}>
            <Form className="antd-form-custom-style" ref={this.formRef}>
              <fieldset style={{ width: '100%' }}>
                <legend>
                  {this.state.selectedUser.id ? (
                    <span style={{ color: '#e74c3c' }}>
                      <FormattedMessageWrapper
                        id="Modification coordonnées"
                        values={{
                          firstName: this.state.selectedUser.firstName,
                          lastName: this.state.selectedUser.lastName,
                        }}
                      />{' '}
                    </span>
                  ) : (
                    <span style={{ color: '#27ae60' }}>
                      <FormattedMessageWrapper id="Création d'un nouvel utilisateur" />
                    </span>
                  )}
                </legend>

                <Row>
                  <Col lg={12} md={24} span={12}>
                    <FormItem label="Société" name="company">
                      <Input placeholder="Société" />
                    </FormItem>
                  </Col>
                </Row>
                <Row>
                  <Col lg={12} md={24} span={12}>
                    <FormItem label="Nom" name="lastName">
                      <Input placeholder="Nom" />
                    </FormItem>
                  </Col>
                  <Col lg={12} md={24} span={12}>
                    <FormItem label="Prénom" name="firstName">
                      <Input placeholder="Prénom" />
                    </FormItem>
                  </Col>
                </Row>
                <Row>
                  <Col lg={12} md={24} span={12}>
                    <FormItem label="Nom d'utilisateur" name="username">
                      <Input autoComplete="off" placeholder="Nom d'utilisateur" />
                    </FormItem>
                  </Col>
                  <Col lg={12} md={24} span={12}>
                    <FormItem label="Téléphone" name="phone" required={false}>
                      <Input placeholder="Téléphone" />
                    </FormItem>
                  </Col>
                </Row>
                <Row>
                  <Col lg={12} md={24} span={12}>
                    <FormItem label="Mot de passe" name="plainPassword" required={false}>
                      <InputPassword autoComplete="new-password" placeholder="Mot de passe" />
                    </FormItem>
                  </Col>
                  <Col lg={12} md={24} span={12}>
                    <FormItem
                      label="Confirmation du mot de passe"
                      name="plainPasswordConfirmation"
                      required={false}
                      rules={[
                        (form: FormInstance) => ({
                          message: (
                            <FormattedMessageWrapper id="Confirmation mot de passe incorrecte !" />
                          ),
                          validator: (_: any, value: any) => {
                            if (value && value !== form?.getFieldValue('plainPassword')) {
                              return Promise.reject();
                            }

                            return Promise.resolve();
                          },
                        }),
                      ]}
                    >
                      <InputPassword autoComplete="new-password" placeholder="Confirmation du mot de passe" />
                    </FormItem>
                  </Col>
                  <Col lg={12} md={24} span={12}>
                    <FormItem label="Préférence linguistique" name="languagePreference">
                      <Select
                        options={[
                          {
                            label: <FormattedMessageWrapper id="Aucune" />,
                            value: null,
                          },
                          {
                            label: FR.label,
                            value: FR.value,
                          },
                          {
                            label: EN.label,
                            value: EN.value,
                          },
                        ]}
                        placeholder="Préférence linguistique"
                        style={{ width: '100%' }}
                      />
                    </FormItem>
                  </Col>
                </Row>
              </fieldset>
            </Form>
          </Col>
        </Row>
      </div>
    );
  }

  public renderOrganizerSelector = () => (
    <Row>
      <Col className="no-margin" span={24}>
        <div className="input-label">
          <FormattedMessageWrapper id="Rechercher un utilisateur existant :" />
        </div>
      </Col>
      <Col className="reverse-margin" lg={14} md={24} span={14}>
        <Select
          defaultActiveFirstOption={false}
          filterOption={false}
          notFoundContent={<FormattedMessageWrapper id="Aucun résultat" />}
          onChange={this.handleSelectChange}
          onSearch={this.fetchSelectData}
          options={this.userCollection.map((userModel: UserModel) => ({
            label: `${userModel.company.toUpperCase()} - ${userModel.firstName} ${userModel.lastName}`,
            value: userModel.id,
          }))}
          placeholder="Rechercher un utilisateur"
          showArrow={false}
          showSearch
          style={{ width: '100%' }}
          value={this.state.selectedUser.id}
        />
      </Col>
      <Col className="reverse-margin" lg={4} md={24} span={4}>
        <div style={{ lineHeight: 2, textAlign: 'center' }}>
          <FormattedMessageWrapper id="ou" />
        </div>
      </Col>
      <Col className="reverse-margin" lg={6} md={24} span={6} style={{ textAlign: 'center' }}>
        <Button className="btn-clear-data" htmlType="button" key="clear" onClick={this.clearData}>
          <FormattedMessageWrapper id="Nouvel utilisateur" />
        </Button>
      </Col>
    </Row>
  );

  public submit = async () => {
    this.setState({ loading: true });

    const { onSuccess, show, userShow } = this.props;

    try {
      const values = await this.formRef.current?.validateFields();

      const userToSave = this.state.selectedUser;

      await userToSave.patch({
        ...values,
        role: this.roleCollection.first()?.get('@id'),
      });

      await userShow.patch({
        show: show.iri,
        user: userToSave.iri,
      });

      messageSuccessIntl("Enregistrement réussi !");

      if (onSuccess) {
        onSuccess();
      }

      this.formRef.current?.resetFields();
    } catch (error) {
      handleFormError(error, this.formRef.current);
    } finally {
      this.setState({ loading: false });
    }
  };

  private clearData = () => {
    this.formRef.current?.resetFields();
    this.setState({ selectedUser: new UserModel() });
    this.userCollection.clear();
  };

  private fetchSelectData = async (value: string) => {
    this.userCollection.clear();

    if (value.length >= 2) {
      await this.userCollection
        .setFilters({
          'role.reference': ROLE_ORGANIZER,
          search: value,
        })
        .list();
    }
  };

  private handleSelectChange = (userId: id) => {
    const selectedUser = this.userCollection.find(user => user.id === userId) as UserModel;

    if (selectedUser) {
      this.formRef.current?.setFieldsValue({
        company: selectedUser.company,
        email: selectedUser.email,
        firstName: selectedUser.firstName,
        languagePreference: selectedUser.languagePreference,
        lastName: selectedUser.lastName,
        phone: selectedUser.phone,
        username: selectedUser.username,
      });

      this.setState({ selectedUser });
    }
  };

  private removeOrganizer = async () => {
    const { onSuccess, userShow } = this.props;
    if (userShow.id) {
      try {
        await userShow.destroy();
        if (onSuccess) {
          onSuccess();
        }
      } catch (error) {
        handleApiError(error);
      }
    }
  };

  private setInitialValues = async () => {
    const { user } = this.props;

    this.formRef.current?.setFieldsValue({
      company: user.company,
      email: user.email,
      firstName: user.firstName,
      languagePreference: user.languagePreference,
      lastName: user.lastName,
      phone: user.phone,
      username: user.username,
    });

    this.setState({ selectedUser: user });
  };

  private updateFooter = () => {
    const { closeModal, userShow } = this.props;
    const { loading } = this.state;

    this.props.setModalFooter(
      [
        <Button
          className="modal-btn-back"
          htmlType="button"
          key="back"
          loading={loading}
          onClick={closeModal}
        >
          <FormattedMessageWrapper id="Annuler" />
        </Button>,

        userShow.id ? (
          <Button
            htmlType="button"
            key="delete"
            loading={loading}
            onClick={this.removeOrganizer}
          >
            <FormattedMessageWrapper id="Retirer l'utilisateur" />
          </Button>
        ) : null,

        <Button
          htmlType="button"
          key="ok"
          loading={loading}
          onClick={this.submit}
          style={{ float: 'right' }}
          type="primary"
        >
          <FormattedMessageWrapper id="Enregistrer" />
        </Button>,
      ].filter(Boolean) as React.ReactNode[],
    );
  };
}

export default OrganizerForm;
